import { type FC, type Ref } from "react";
import { cn } from "../../lib/utils";
import { Badge, Button, Icon, type IconType, Text } from "../../primitives";
import type { SideDrawerTriggerProps } from "./useSideDrawer";

export type SideDrawerTriggerViewProps = SideDrawerTriggerProps & {
	count: number;
	label: string;
	iconType: IconType;
	/** When true, `label` renders beside the icon. Otherwise the button is icon-only. */
	showLabel?: boolean;
	/** Accessible name ahead of the selected count. Defaults to `Select ${label.toLowerCase()}`. */
	ariaLabel?: string;
	className?: string;
	ref?: Ref<HTMLButtonElement>;
};

export const SideDrawerTrigger: FC<SideDrawerTriggerViewProps> = ({
	count,
	label,
	iconType,
	showLabel = false,
	ariaLabel = `Select ${label.toLowerCase()}`,
	isOpen,
	className,
	ref,
	...triggerProps
}) => (
	<Button
		ref={ref}
		variant="ghost"
		size="sm"
		aria-label={`${ariaLabel}, ${count} selected`}
		aria-expanded={isOpen}
		{...triggerProps}
		leadingIcon={<Icon type={iconType} size="mini" color={isOpen ? "brand" : "secondary"} />}
		className={cn(
			"w-7 rounded-md border bg-surface-base px-0",
			isOpen
				? "border-brand-primary text-brand-primary"
				: "border-bc-default text-secondary hover:border-brand-primary hover:text-brand-primary",
			showLabel && "w-fit px-2",
			className,
		)}
	>
		<Badge count={count} />
		{showLabel && <Text type="body-strong">{label}</Text>}
	</Button>
);
